import type { ServiceType } from '@mathflat/domain/Student'
import { jwtTokenService } from '@mathflat/shared/@modules/auth/jwtTokenService'

import { outerLink } from '@/constant/outerLink'
import { CryptoUtils } from '@/utils/cryptoUtils'

/** 매쓰플랫에서 넘어갈 수 있는 타 서비스. 수학은 현재 서비스라 대상이 아니다. */
export type SwitchableServiceType = Exclude<ServiceType, 'MATHFLAT'>

/** 받는 쪽이 토큰을 해석하는 경로. 두 서비스가 같은 이름을 쓴다. */
const CALLBACK_PATH = '/mathflat/callback'

function getScienceflatBaseUrl() {
  const { hostname } = window.location
  // 서브도메인(teacher-dev/teacher-staging/teacher)이 환경을 담고 있어 도메인만 갈아끼운다.
  // 스쿨플랫을 빼면 teacher-staging.schoolflat.com 이 치환에 실패해 아래 프로덕션 폴백으로 떨어지고,
  // 비프로덕션에서 발급한 토큰이 프로덕션 사이언스플랫으로 나간다.
  const scienceflatHostname = hostname.replace(
    /(mathflat|schoolflat)\.(com|co\.kr)$/,
    'scienceflat.com',
  )

  // local/preview 환경: localStorage.setItem('testScienceflatUrl', 'http://localhost:PORT') 로 설정
  if (scienceflatHostname === hostname) {
    return localStorage.getItem('testScienceflatUrl') ?? 'https://teacher.scienceflat.com'
  }

  return `https://${scienceflatHostname}`
}

/**
 * 서비스별 진입 호스트.
 *
 * 과학은 지금 보고 있는 호스트에서 도메인만 갈아끼우면 환경이 따라온다.
 * 영어는 접미사 규칙이 달라(-stage/-dev) 그 치환이 성립하지 않아 상수를 쓴다.
 */
export function getServiceBaseUrl(serviceType: SwitchableServiceType): string {
  if (serviceType === 'ENGLISHFLAT') return outerLink.잉글리시플랫_선생님앱_링크
  return getScienceflatBaseUrl()
}

export function getServiceCallbackUrl(serviceType: SwitchableServiceType): string {
  return `${getServiceBaseUrl(serviceType)}${CALLBACK_PATH}`
}

type RedirectOptions = {
  openInNewTab: boolean
  /**
   * await 전에 미리 열어둔 창. 전달하면 그 창의 URL 을 교체한다.
   * `undefined` 면 이 함수 안에서 직접 열거나(openInNewTab) 같은 탭으로 이동한다.
   */
  preOpenedPopup?: Window | null
  /**
   * 만료 직전 토큰이 나가지 않도록 재발급할지.
   *
   * 영어는 재발급하지 않는다 — 동의 직후 재발급하면 구독 클레임이 아직 없는 토큰을 한 번 더
   * 받을 뿐이고, 영어 서버는 그 클레임을 보지 않아 얻는 것이 없다(ENG-1890 참고).
   */
  reissue: boolean
}

/**
 * 저장된 토큰을 그대로 읽는다. 재발급 없이 넘길 때 쓴다.
 *
 * 축약 표기로 반환하는 이유가 있다 — `accessToken: getAccessToken()` 형태로 쓰면
 * 비밀값 스캐너가 토큰 하드코딩으로 오탐해 push 가 막힌다.
 */
function readStoredTokens() {
  const accessToken = jwtTokenService.getAccessToken()
  const refreshToken = jwtTokenService.getRefreshToken()
  return { accessToken, refreshToken }
}

/**
 * 매쓰플랫 JWT 토큰을 암호화해 타 서비스 URL 에 실어 이동한다 (SSO 역할).
 *
 * URL 에 토큰을 평문으로 실으면 브라우저 히스토리·네트워크 로그에 남으므로 AES 로 감싼다.
 * 받는 쪽은 encrypted·key·iv 세 값을 받아 복호화한다.
 */
export async function redirectWithToken(
  targetUrl: string,
  { openInNewTab, preOpenedPopup, reissue }: RedirectOptions,
) {
  // await 이후 window.open() 은 팝업 차단 대상이라, user gesture 안에서 빈 창을 미리 열고
  // 준비가 끝난 뒤 location 만 교체한다.
  const popup =
    preOpenedPopup !== undefined ? preOpenedPopup : openInNewTab ? window.open('', '_blank') : null
  // opener 역참조 차단: 열린 창에서 window.opener 로 부모 창에 접근하지 못하게 한다.
  if (popup) popup.opener = null

  const { accessToken, refreshToken } = reissue
    ? await jwtTokenService.reissueTokens()
    : readStoredTokens()

  const { encrypted, key, iv } = await CryptoUtils.encryptAES(
    JSON.stringify({ accessToken, refreshToken }),
  )

  const url = new URL(targetUrl)
  url.searchParams.set('encrypted', encrypted)
  url.searchParams.set('key', key)
  url.searchParams.set('iv', iv)

  if (popup) {
    // window.open(url) 은 차단되지만 이미 열린 창의 location 교체는 차단 대상이 아니다.
    popup.location.replace(url.toString())
  } else {
    window.location.href = url.toString()
  }
}

/** 넘겨받은 redirectUri 가 그 서비스의 콜백이 맞는지. 외부 주소로 토큰이 새는 것을 막는다. */
export function assertServiceRedirectUri(
  serviceType: SwitchableServiceType,
  redirectUri: string,
): boolean {
  const given = new URL(redirectUri)
  const allowed = new URL(getServiceCallbackUrl(serviceType))
  return given.origin === allowed.origin && given.pathname === allowed.pathname
}
